/**
 * Employee RBAC Permissions
 *
 * Defines what employees can and cannot access in the admin panel
 * Admins (admin/super_admin) have full access to everything
 */

import { createClient } from '@/lib/supabase/server';

export interface EmployeeProfile {
  id: string;
  user_id: string;
  tenant_id: string;
  role: 'tenant_owner' | 'employee';
  is_active: boolean;
}

/**
 * Check if user is admin or super_admin
 */
export async function isAdmin(userId: string): Promise<boolean> {
  try {
    const supabase = await createClient();

    // Fortune 500 RBAC pattern: Query tenant_user_roles instead of users.role
    const { data: roleData, error } = await supabase
      .from('tenant_user_roles')
      .select(`
        roles!inner (
          name
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active')
      .eq('is_primary', true)
      .single();

    if (error || !roleData) {
      return false;
    }

    const roleName = (roleData?.roles as any)?.name;
    // Admin or super_admin have full access
    return roleName === 'admin' || roleName === 'super_admin';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Get employee profile for current user
 */
export async function getEmployeeProfile(userId: string): Promise<EmployeeProfile | null> {
  try {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from('employee_profiles')
      .select('id, user_id, tenant_id, role, is_active')
      .eq('user_id', userId)
      .eq('is_active', true)
      .is('deleted_at', null)
      .single();

    if (error || !data) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching employee profile:', error);
    return null;
  }
}

/**
 * Check if user is an employee (not tenant owner)
 */
export async function isEmployee(userId: string): Promise<boolean> {
  const profile = await getEmployeeProfile(userId);
  return profile?.role === 'employee';
}

/**
 * Check if user is a tenant owner
 */
export async function isTenantOwner(userId: string): Promise<boolean> {
  const profile = await getEmployeeProfile(userId);
  return profile?.role === 'tenant_owner';
}

/**
 * Admin routes that employees CAN access
 */
export const EMPLOYEE_ALLOWED_ROUTES = [
  '/admin', // Dashboard
  '/admin/services', // Their own services
  '/admin/hours', // Their business hours
  '/admin/calendar', // Their bookings calendar
  '/admin/calendar-sync', // Calendar integration (Google/Apple)
  '/admin/settings/calendar', // Calendar settings
  '/admin/bookings', // Their bookings
];

/**
 * Admin routes that ONLY owners can access
 */
export const OWNER_ONLY_ROUTES = [
  '/admin/employees', // Employee management
  '/admin/businesses', // Business management
  '/admin/users', // User management
  '/admin/roles', // Role management
  '/admin/permissions', // Permission management
  '/admin/subscription', // Subscription management
  '/admin/migrations', // Migrations
  '/admin/debug-rbac', // Debug RBAC
  '/admin/tenants', // Tenant management
  '/admin/settings', // Global settings (owner only)
];

/**
 * Check if employee can access a route
 */
export function canEmployeeAccessRoute(route: string): boolean {
  // Exact match
  if (EMPLOYEE_ALLOWED_ROUTES.includes(route)) {
    return true;
  }

  // Check if route starts with allowed path
  return EMPLOYEE_ALLOWED_ROUTES.some(allowedRoute =>
    route.startsWith(allowedRoute)
  );
}

/**
 * Check if route is owner-only
 */
export function isOwnerOnlyRoute(route: string): boolean {
  return OWNER_ONLY_ROUTES.some(ownerRoute =>
    route.startsWith(ownerRoute)
  );
}

/**
 * Get sidebar items for employees
 */
export function getEmployeeSidebarItems() {
  return [
    {
      title: 'Dashboard',
      href: '/admin',
      icon: 'LayoutDashboard',
    },
    {
      title: 'My Services',
      href: '/admin/services',
      icon: 'Briefcase',
    },
    {
      title: 'My Bookings',
      href: '/admin/calendar',
      icon: 'Calendar',
    },
    {
      title: 'Business Hours',
      href: '/admin/hours',
      icon: 'Clock',
    },
    {
      title: 'Calendar Sync',
      href: '/admin/calendar-sync',
      icon: 'RefreshCw',
      description: 'Connect Google or Apple Calendar',
    },
  ];
}

/**
 * Get sidebar items for tenant owners
 */
export function getOwnerSidebarItems() {
  return [
    {
      title: 'Dashboard',
      href: '/admin',
      icon: 'LayoutDashboard',
    },
    {
      title: 'Services',
      href: '/admin/services',
      icon: 'Briefcase',
    },
    {
      title: 'Bookings',
      href: '/admin/calendar',
      icon: 'Calendar',
    },
    {
      title: 'Business Hours',
      href: '/admin/hours',
      icon: 'Clock',
    },
    {
      title: 'Calendar Sync',
      href: '/admin/calendar-sync',
      icon: 'RefreshCw',
    },
    {
      title: 'Employees',
      href: '/admin/employees',
      icon: 'Users',
      badge: 'NEW',
    },
    {
      title: 'Settings',
      href: '/admin/settings',
      icon: 'Settings',
    },
  ];
}
